import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import api from '../utils/api';
import toast from 'react-hot-toast';
import './Dashboard.css';

// ── Account status tabs ────────────────────────────────────────────────────
const STATUS_TABS = [
  { key: 'active',   label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
  { key: 'breached', label: 'Breached' },
];

// ── Account card (FundedNext-style) ────────────────────────────────────────
function AccountCard({ challenge }) {
  const pnl     = challenge.currentBalance - challenge.startingBalance;
  const pnlPct  = ((pnl / challenge.startingBalance) * 100).toFixed(2);
  const isPos   = pnl >= 0;
  const drawdown = challenge.peakBalance
    ? ((challenge.peakBalance - challenge.currentBalance) / challenge.peakBalance * 100).toFixed(2)
    : '0.00';
  const statusLabel = challenge.status === 'phase2' ? 'Phase 2' : challenge.status;
  const isActive    = ['active','phase2'].includes(challenge.status);
  const isPassed    = challenge.status === 'passed';

  // Generate a display account number
  const acctNum = `AV-${challenge._id?.slice(-8).toUpperCase() || '00000000'}`;

  return (
    <div className={`account-card ${isPassed ? 'account-passed' : challenge.status === 'failed' ? 'account-breached' : ''}`}>
      {/* Card header */}
      <div className="ac-header">
        <div className="ac-header-left">
          <div className="ac-plan-name">{challenge.plan?.name || 'Challenge'}</div>
          <div className="ac-acct-num">{acctNum}</div>
        </div>
        <div className={`ac-status-pill ${challenge.status === 'phase2' ? 'active' : challenge.status}`}>
          {statusLabel}
        </div>
      </div>

      {/* Info grid */}
      <div className="ac-info-grid">
        <div className="ac-info-item">
          <span className="ac-info-label">Server</span>
          <span className="ac-info-value">AlphaVault-Live</span>
        </div>
        <div className="ac-info-item">
          <span className="ac-info-label">Equity</span>
          <span className="ac-info-value mono">
            ${challenge.currentBalance?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </span>
        </div>
        <div className="ac-info-item">
          <span className="ac-info-label">Account Type</span>
          <span className="ac-info-value">{challenge.challengeType === '2-step' ? '2-Step' : '1-Step'} Evaluation</span>
        </div>
        <div className="ac-info-item">
          <span className="ac-info-label">Balance</span>
          <span className="ac-info-value mono">
            ${challenge.startingBalance?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </span>
        </div>
      </div>

      {/* Metrics row */}
      <div className="ac-metrics">
        <div className="ac-metric">
          <span className="ac-metric-label">P&L</span>
          <span className={`ac-metric-value mono ${isPos ? 'text-green' : 'text-red'}`}>
            {isPos ? '+' : ''}{pnlPct}%
          </span>
        </div>
        <div className="ac-metric">
          <span className="ac-metric-label">Drawdown</span>
          <span className={`ac-metric-value mono ${parseFloat(drawdown) > 7 ? 'text-red' : ''}`}>
            {drawdown}%
          </span>
        </div>
        <div className="ac-metric">
          <span className="ac-metric-label">Split</span>
          <span className="ac-metric-value mono text-accent">{challenge.currentSplitPct || 70}%</span>
        </div>
        <div className="ac-metric">
          <span className="ac-metric-label">Days</span>
          <span className="ac-metric-value mono">{challenge.tradingDays || 0}</span>
        </div>
      </div>

      {/* Phase stepper for 2-step */}
      {challenge.challengeType === '2-step' && isActive && (
        <div className="ac-phase-bar">
          <div className={`ac-phase-step ${challenge.currentPhase >= 1 ? 'done' : ''}`}>Phase 1</div>
          <div className="ac-phase-line" />
          <div className={`ac-phase-step ${challenge.currentPhase >= 2 || challenge.status === 'phase2' ? 'active' : ''}`}>Phase 2</div>
          <div className="ac-phase-line" />
          <div className={`ac-phase-step ${isPassed ? 'done' : ''}`}>Funded</div>
        </div>
      )}

      {/* Bot indicator */}
      {challenge.botRunning && (
        <div className="ac-bot-badge">
          <span className="blink">●</span> AI Bot Active
        </div>
      )}

      {/* Payout row */}
      {isPassed && (
        <div className="ac-payout">
          <span className="text-green">Challenge Passed</span>
          <span className={`ac-payout-status ${challenge.payoutStatus}`}>
            Payout: {challenge.payoutStatus}
          </span>
        </div>
      )}

      {/* Footer */}
      <div className="ac-footer">
        <Link to={`/challenge/${challenge._id}`} className="btn btn-primary btn-sm">
          Open Dashboard
        </Link>
        {isActive && (
          <Link to="/sim-trade" className="btn btn-outline btn-sm">
            Trade
          </Link>
        )}
      </div>
    </div>
  );
}

// ── Stats overview card ────────────────────────────────────────────────────
function StatsOverview({ challenges }) {
  const active  = challenges.filter(c => ['active','phase2'].includes(c.status)).length;
  const passed  = challenges.filter(c => c.status === 'passed').length;
  const failed  = challenges.filter(c => c.status === 'failed').length;
  const totalProfit = challenges
    .filter(c => c.status === 'passed')
    .reduce((acc, c) => acc + Math.max(0, c.currentBalance - c.startingBalance), 0);
  const totalEquity = challenges
    .filter(c => ['active','phase2'].includes(c.status))
    .reduce((acc, c) => acc + (c.currentBalance || 0), 0);

  return (
    <div className="stats-overview">
      <div className="so-item">
        <span className="so-value mono">{active}</span>
        <span className="so-label">Active</span>
      </div>
      <div className="so-divider" />
      <div className="so-item">
        <span className="so-value mono text-green">{passed}</span>
        <span className="so-label">Passed</span>
      </div>
      <div className="so-divider" />
      <div className="so-item">
        <span className="so-value mono text-red">{failed}</span>
        <span className="so-label">Breached</span>
      </div>
      <div className="so-divider" />
      <div className="so-item">
        <span className="so-value mono" style={{ color: 'var(--accent)' }}>
          ${totalEquity.toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </span>
        <span className="so-label">Total Equity</span>
      </div>
      <div className="so-divider" />
      <div className="so-item">
        <span className="so-value mono" style={{ color: 'var(--gold)' }}>
          ${totalProfit.toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </span>
        <span className="so-label">Total Profit</span>
      </div>
    </div>
  );
}

// ── Promo sidebar card ─────────────────────────────────────────────────────
function PromoSidebar() {
  return (
    <div className="promo-sidebar">
      <div className="promo-card card">
        <div className="promo-badge">NEW</div>
        <h3>AI Trading Bots</h3>
        <p>Let our AI manage your challenge account with advanced strategies and real-time market analysis.</p>
        <Link to="/bots" className="btn btn-primary btn-sm" style={{ width: '100%' }}>
          Explore Bots
        </Link>
      </div>

      <div className="promo-card card">
        <h3>Sim Trading Engine</h3>
        <p>Practice with live candlestick charts, leverage up to 50x, and real-time P&L tracking.</p>
        <Link to="/sim-trade" className="btn btn-outline btn-sm" style={{ width: '100%' }}>
          Open Terminal
        </Link>
      </div>

      <div className="promo-card card promo-rules">
        <h3>Trading Rules & Guidelines</h3>
        <ul>
          <li>Max Daily Drawdown: 5%</li>
          <li>Max Overall Drawdown: 10%</li>
          <li>Profit Target: 8-10%</li>
          <li>Min Trading Days: 5-10</li>
          <li>No martingale or grid bots</li>
        </ul>
        <Link to="/pricing" className="btn btn-outline btn-sm" style={{ width: '100%', marginTop: 12 }}>
          View All Plans
        </Link>
      </div>
    </div>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────────────
export default function Dashboard() {
  const { user }        = useAuth();
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [activeTab, setActiveTab]   = useState('active');
  const [search, setSearch]         = useState('');

  useEffect(() => {
    api.get('/challenges')
      .then(r => setChallenges(r.data.challenges))
      .catch(() => toast.error('Failed to load challenges'))
      .finally(() => setLoading(false));
  }, []);

  // Filter challenges by tab
  const filtered = useMemo(() => {
    let list;
    switch (activeTab) {
      case 'active':
        list = challenges.filter(c => ['active','phase2','passed'].includes(c.status));
        break;
      case 'inactive':
        list = challenges.filter(c => c.status === 'inactive' || c.status === 'expired');
        break;
      case 'breached':
        list = challenges.filter(c => c.status === 'failed');
        break;
      default:
        list = challenges;
    }

    // Search filter
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        (c.plan?.name || '').toLowerCase().includes(q) ||
        c._id?.toLowerCase().includes(q) ||
        (c.challengeType || '').toLowerCase().includes(q)
      );
    }

    return list;
  }, [challenges, activeTab, search]);

  const tabCounts = {
    active:   challenges.filter(c => ['active','phase2','passed'].includes(c.status)).length,
    inactive: challenges.filter(c => c.status === 'inactive' || c.status === 'expired').length,
    breached: challenges.filter(c => c.status === 'failed').length,
  };

  return (
    <div className="app-layout">
      <Sidebar />
      <main className="main-content dash-main">
        {/* Header */}
        <div className="dash-header">
          <div>
            <h1>Accounts</h1>
            <p className="text-secondary" style={{ marginTop: 4 }}>
              Welcome back, {user?.firstName || user?.name?.split(' ')[0] || 'Trader'}
            </p>
          </div>
          <Link to="/pricing" className="btn btn-primary">+ New Challenge</Link>
        </div>

        {/* Stats overview */}
        {challenges.length > 0 && <StatsOverview challenges={challenges} />}

        {/* Content area with promo sidebar */}
        <div className="dash-content-layout">
          <div className="dash-main-col">
            {/* Type tabs (Crypto) */}
            <div className="dash-type-tabs">
              <button className="dash-type-tab active">Crypto</button>
            </div>

            {/* Status tabs + search */}
            <div className="dash-controls">
              <div className="dash-status-tabs">
                {STATUS_TABS.map(tab => (
                  <button
                    key={tab.key}
                    className={`dash-status-tab ${activeTab === tab.key ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label}
                    {tabCounts[tab.key] > 0 && (
                      <span className="tab-count">{tabCounts[tab.key]}</span>
                    )}
                  </button>
                ))}
              </div>
              <div className="dash-search">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                </svg>
                <input
                  type="text"
                  placeholder="Search accounts..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
            </div>

            {/* Accounts grid */}
            {loading ? (
              <div className="dash-loading">
                <div className="loading-pulse" />
                <p>Loading your accounts...</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="dash-empty card">
                <div className="dash-empty-icon">
                  {activeTab === 'breached' ? '⚠' : activeTab === 'inactive' ? '⏸' : '◈'}
                </div>
                <h3>No {activeTab} accounts</h3>
                <p>
                  {activeTab === 'active'
                    ? 'Purchase a challenge to start trading.'
                    : activeTab === 'breached'
                    ? 'No breached accounts. Keep up the good work!'
                    : 'No inactive accounts found.'}
                </p>
                {activeTab === 'active' && (
                  <Link to="/pricing" className="btn btn-primary btn-sm" style={{ marginTop: 16 }}>
                    Browse Challenges
                  </Link>
                )}
              </div>
            ) : (
              <div className="accounts-grid">
                {filtered.map(c => <AccountCard key={c._id} challenge={c} />)}
              </div>
            )}
          </div>

          {/* Promo sidebar */}
          <PromoSidebar />
        </div>

        {/* Free trial CTA for new users */}
        {challenges.length === 0 && !loading && (
          <div className="card free-trial-cta">
            <div className="ftc-icon">🎁</div>
            <h3>Start with a Free Trial</h3>
            <p>Get a $10K funded account for 10 days — no payment needed.</p>
            <Link to="/pricing" className="btn btn-primary btn-sm" style={{ marginTop: 12 }}>
              Claim Free Trial →
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}
